import { useState } from "react";
import { ArrowsClockwise, CheckCircle } from "@phosphor-icons/react";
import api from "../../../lib/api";
import { useAuth } from "../../../context/AuthContext";
import { btnGhost, btnPrimary, fmtDateTimeWIB, fmtIDR, inp, todayWIB } from "../util";
import { CHANNELS, Card, JStatus, Pill, Rate, Row, call, useLoad } from "./common";
import { TaskStatus } from "./Tasks";
import { ComplianceCard, PendingBoard } from "./OpsBoards";
import ReconCard from "./Recon";
import DailyCheck from "./ai/DailyCheck";

function SalesCard({ s, open, reload, nr }) {
    if (!s) return <Card title="Sales Closing" testid="dash-sales"><p className="text-sm text-neutral-400">{nr ? "Tidak Wajib Lapor — transaksi masuk laporan hari kerja berikutnya." : "Belum ada Sales Closing."}</p></Card>;
    const t = s.totals || {};
    return (
        <Card title="Sales Closing" testid="dash-sales" right={<Pill status={s.status} testid="dash-sales-status" />}>
            <div className="font-serif text-3xl tabular-nums" data-testid="dash-group-total">{fmtIDR(t.grand_total_idr)}</div>
            {s.period?.start_at && <p className="text-[11px] text-neutral-500" data-testid="dash-sales-period">Periode: {s.period.label}</p>}
            <Row label="Ready Stock" value={fmtIDR(t.ready_sales_idr)} testid="dash-ready" /><Row label="PO Baru" value={fmtIDR(t.po_sales_idr)} testid="dash-po" /><Row label="Total order" value={t.total_orders} />
            <div className="mt-2">{CHANNELS.map(([k, l]) => <Row key={k} label={`Jubelio · ${l}`} value={<span><JStatus s={s.jubelio_checks?.[k]?.status} testid={`dash-j-${k}`} />{s.jubelio_checks?.[k]?.status === "SELISIH" && <span className="block text-[11px] text-rose-700">selisih {fmtIDR(s.jubelio_checks[k].diff_sales_idr)} / {s.jubelio_checks[k].diff_orders} order</span>}</span>} />)}</div>
            <div className="flex gap-2 mt-3">
                {s.status !== "APPROVED" && s.first_submitted_at && <button type="button" className={btnGhost} data-testid="dash-recheck-btn" onClick={async () => { if (await call(() => api.post(`/reporting/v2/sales-closing/${s.id}/recheck`), "Jubelio diperiksa ulang")) reload(); }}><ArrowsClockwise size={14} /> Periksa Ulang Jubelio</button>}
                <button type="button" className={btnGhost} onClick={() => open("sales", { tab: "closing", date: s.reporting_date })} data-testid="dash-open-sales">Buka detail</button>
            </div>
        </Card>
    );
}

function FinanceCard({ f, open, date }) {
    const t = f?.totals;
    return (
        <Card title="Finance Cash In" testid="dash-finance" right={<Pill status={f?.status || "DRAFT"} />}>
            <Row label={`Sesi 1 · ${f.windows.session1}`} value={t ? fmtIDR(t.session1.amount_idr) : "—"} testid="dash-fin-s1" />
            <Row label={`Sesi 2 · ${f.windows.session2}`} value={t ? fmtIDR(t.session2.amount_idr) : "—"} testid="dash-fin-s2" />
            <Row label="Total Cash In" value={t ? fmtIDR(t.total_idr) : "—"} bold testid="dash-fin-total" />
            <p className="text-[11px] text-neutral-500 mt-2">Rekonsiliasi: Tidak Dapat Dibandingkan — belum ada sumber uang masuk pembanding yang setara.</p>
            <button type="button" className={`${btnGhost} mt-3`} onClick={() => open("finance", { date })} data-testid="dash-open-finance">Buka detail</button>
        </Card>
    );
}

function Waiting({ items, open, reload }) {
    if (!items?.length) return null;
    return (
        <section className="bg-white border border-sky-200 rounded-sm p-4 md:col-span-2" data-testid="dash-waiting">
            <h3 className="text-base font-semibold">Menunggu Approval <span className="text-sm font-normal text-neutral-500">· {items.length}</span></h3>
            <div className="grid sm:grid-cols-2 gap-2 mt-2">
                {items.map((w) => (
                    <div key={w.id} role="button" tabIndex={0} onClick={() => open("sales", { tab: "pic", date: w.reporting_date, id: w.id })} className="border border-mist rounded-sm p-3 flex items-center justify-between gap-3 cursor-pointer hover:border-ink" data-testid={`dash-waiting-${w.user_name}`}>
                        <div className="text-sm">
                            <div className="font-semibold">{w.user_name}</div>
                            <div className="text-xs text-neutral-500">Report PIC · {new Date(`${w.reporting_date}T00:00:00`).toLocaleDateString("id-ID", { day: "numeric", month: "short", year: "numeric" })}</div>
                            <div className="text-xs mt-1">Ready {w.ready_rate}% · PO {w.po_rate}% · Follow-up pending: {w.fu_belum}</div>
                        </div>
                        <button type="button" className={btnPrimary} data-testid={`dash-approve-${w.user_name}`} onClick={async (e) => { e.stopPropagation(); if (await call(() => api.post(`/reporting/v2/pic/${w.id}/approve`), "Report PIC disetujui")) reload(); }}><CheckCircle size={14} /> Setujui</button>
                    </div>
                ))}
            </div>
        </section>
    );
}

function PicCard({ list, open, date }) {
    return (
        <Card title={`Report PIC · ${list.length} laporan`} testid="dash-pic">
            {list.length === 0 && <p className="text-sm text-neutral-400">Belum ada Report PIC.</p>}
            {list.map((p) => (
                <button key={p.id} type="button" onClick={() => open("sales", { tab: "pic", date, id: p.id })} className="w-full text-left py-2 border-b border-mist/60 text-sm" data-testid={`dash-pic-${p.user_name}`}>
                    <div className="flex items-center justify-between gap-2"><b>{p.user_name}</b>{p.status === "SUBMITTED" ? <span className="text-xs font-semibold text-sky-700">Menunggu Approval</span> : <Pill status={p.status} />}</div>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs mt-1">
                        <span>Ready <Rate value={p.ready_rate} lvl={p.ready_level} /></span><span>PO <Rate value={p.po_rate} lvl={p.po_level} /></span>
                        <span className="text-neutral-500">Target: {p.monthly?.target_idr ? `${p.monthly.achievement_pct}% · kurang ${fmtIDR(p.monthly.gap_idr)}` : "belum diatur"}</span>
                    </div>
                </button>
            ))}
        </Card>
    );
}

function TaskCard({ t }) {
    return (
        <Card title="Follow-up & Handover" testid="dash-tasks">
            <div className="grid grid-cols-3 gap-2 text-center mb-3">
                {[["Pending", t.pending, "dash-t-pending"], ["Overdue", t.overdue.length, "dash-t-overdue"], ["Done", t.done, "dash-t-done"]].map(([l, v, id]) => (
                    <div key={l} className="border border-mist rounded-sm py-2" data-testid={id}><div className="text-xl tabular-nums">{v}</div><div className="text-[10px] uppercase tracking-widest text-neutral-400">{l}</div></div>
                ))}
            </div>
            {t.overdue.length > 0 && <div className="text-xs mb-2"><b className="text-rose-700">Overdue</b> (perlu dievaluasi Owner/Supervisor, bukan otomatis pelanggaran){t.overdue.map((x) => <div key={x.id}>{x.assignee_name}: {x.customer} — {x.task} (s/d {x.deadline})</div>)}</div>}
            {t.unacknowledged?.length > 0 && <div className="text-xs mb-2" data-testid="dash-t-unack"><b className="text-orange-600">Menunggu Diterima ({t.unacknowledged.length})</b>{t.unacknowledged.map((x) => <div key={x.id}>{x.from_user_name} → {x.assignee_name}: {x.customer} — {x.task}{x.deadline ? ` (s/d ${x.deadline})` : ""}</div>)}</div>}
            <div className="text-[10px] uppercase tracking-widest text-neutral-400 mt-2">Handover terbaru</div>
            {t.recent_handovers.map((x) => <div key={x.id} className="text-xs py-1 flex justify-between gap-2"><span>{x.from_user_name} → {x.assignee_name}: {x.customer} — {x.task}</span><TaskStatus t={x} /></div>)}
            {t.recent_handovers.length === 0 && <p className="text-xs text-neutral-400">Belum ada handover.</p>}
        </Card>
    );
}

export default function Dashboard({ open }) {
    const { user } = useAuth();
    const [date, setDate] = useState(todayWIB());
    const [tick, setTick] = useState(0);
    const [d, reload] = useLoad("/reporting/v2/dashboard", { date });
    return (
        <div className="space-y-4" data-testid="v2-dashboard">
            <div className="flex items-center gap-3"><input type="date" value={date} max={todayWIB()} onChange={(e) => e.target.value && setDate(e.target.value)} className={`${inp} !w-48`} data-testid="dash-date" />
                <button type="button" className={btnGhost} onClick={reload} data-testid="dash-refresh"><ArrowsClockwise size={14} /> Muat ulang</button></div>
            {d && d.date === date && (
                <div className="grid md:grid-cols-2 gap-4">
                    {user.role === "owner" && <Waiting items={d.waiting} open={open} reload={reload} />}
                    {user.role === "owner" && <SalesCard s={d.sales} open={open} reload={reload} nr={d.not_required} />}
                    {user.role === "owner" && <FinanceCard f={d.finance} open={open} date={date} />}
                    <PicCard list={d.pic} open={open} date={date} />
                    <TaskCard t={d.tasks} />
                    <ComplianceCard key={`fu-${date}`} date={date} />
                    {user.role === "owner" && <PendingBoard />}
                    {["owner", "supervisor", "finance"].includes(user.role) && <ReconCard key={`${date}-${tick}`} date={date} role={user.role} />}
                    {["owner", "supervisor"].includes(user.role) && <DailyCheck key={date} date={date} role={user.role} onChanged={() => setTick((t) => t + 1)} />}
                </div>
            )}
            {d?.sales?.first_submitted_at && <p className="text-[11px] text-neutral-400">Sales Closing dikirim {fmtDateTimeWIB(d.sales.first_submitted_at)}</p>}
        </div>
    );
}
